import {
  useEffect,
  useState,
} from 'react';
import { object, oneOf } from 'prop-types';

import imagotipo from 'assets/svg/imagotipo.svg';
import imagotipoDarkH from 'assets/svg/imagotipo-blue-h.svg';
import imagotipoHorizontal from 'assets/svg/imagotipo-horizontal.svg';
import { getSignedUrl } from 'utils/aws/s3';

const DEFAULT_LOGOS = {
  imagotipo,
  'imagotipo-horizontal': imagotipoHorizontal,
  'imagotipo-horizontal-dark': imagotipoDarkH,
};

const SIGNED_URL_EXPIRES_SECONDS = 60 * 60;
// Reuse a signed URL until 5 minutes before it expires.
const SIGNED_URL_TTL_MS = (SIGNED_URL_EXPIRES_SECONDS - 5 * 60) * 1000;
// Keeps the layout while a private logo is being signed (nothing is requested from S3).
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const S3_PATTERN = /^https:\/\/.+\.amazonaws\.com\//;
const ALREADY_SIGNED_PATTERN = /(?:X-Amz-Algorithm|AWSAccessKeyId)=/;

// Signed URLs shared by every <Logo> on the page: { [cleanUrl]: { url, expiresAt } | { promise } }.
const signedCache = new Map();

function readLogos() {
  try {
    const company = JSON.parse(localStorage.getItem('company'));

    return company?.logo || {};
  } catch (e) {
    return {};
  }
}

/** Company logos live in the private bucket under /company/ and must be signed before use. */
export function isPrivateCompanyLogo(url) {
  const value = String(url || '');

  return S3_PATTERN.test(value)
    && /\/company\//.test(value)
    && !ALREADY_SIGNED_PATTERN.test(value);
}

function readCachedSignedUrl(cleanUrl) {
  const entry = signedCache.get(cleanUrl);

  return entry?.url && entry.expiresAt > Date.now() ? entry.url : null;
}

/** Signs a private logo once and shares the result (and the in-flight request) between instances. */
function signLogo(url) {
  const [cleanUrl] = String(url).split('?');
  const cached = readCachedSignedUrl(cleanUrl);

  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = signedCache.get(cleanUrl)?.promise;

  if (pending) {
    return pending;
  }

  const promise = getSignedUrl(cleanUrl, SIGNED_URL_EXPIRES_SECONDS)
    .then((signedUrl) => {
      if (typeof signedUrl !== 'string' || !signedUrl) {
        throw new Error('empty_signed_url');
      }
      signedCache.set(cleanUrl, {
        url: signedUrl,
        expiresAt: Date.now() + SIGNED_URL_TTL_MS,
      });

      return signedUrl;
    })
    .catch((error) => {
      signedCache.delete(cleanUrl);
      throw error;
    });

  signedCache.set(cleanUrl, {
    promise,
  });

  return promise;
}

function initialSource(sourceUrl, fallback) {
  if (!isPrivateCompanyLogo(sourceUrl)) {
    return sourceUrl || fallback;
  }

  // Never put the unsigned private URL in the <img>: S3 answers 403 to it.
  return readCachedSignedUrl(String(sourceUrl).split('?')[0]) || TRANSPARENT_PIXEL;
}

function Logo({ style, variant }) {
  const logos = readLogos();
  const fallback = DEFAULT_LOGOS[variant] || imagotipoDarkH;
  const sourceUrl = {
    imagotipo: logos.imagotipoHorizontal,
    'imagotipo-horizontal': logos.imagotipoHorizontal,
    'imagotipo-horizontal-dark': logos.imagotipoDarkH,
  }[variant];

  const [src, setSrc] = useState(() => initialSource(sourceUrl, fallback));

  useEffect(() => {
    if (!isPrivateCompanyLogo(sourceUrl)) {
      setSrc(sourceUrl || fallback);

      return undefined;
    }

    let isMounted = true;

    signLogo(sourceUrl)
      .then((signedUrl) => isMounted && setSrc(signedUrl))
      // The unsigned URL would only fail again: show the default logo instead.
      .catch(() => isMounted && setSrc(fallback));

    return () => {
      isMounted = false;
    };
  }, [sourceUrl, fallback]);

  return (<img
    alt="Company logo"
    src={src}
    style={{
      height: '100px',
      ...style,
    }}
  />);
}

Logo.defaultProps = {
  company: 'a55',
  variant: 'imagotipo-horizontal-dark',
};

Logo.propTypes = {
  style: object,
  variant: oneOf([
    'imagotipo',
    'imagotipo-horizontal',
    'imagotipo-horizontal-dark',
  ]),
};

export default Logo;

/** @internal Test helper. */
export function clearSignedLogoCache() {
  signedCache.clear();
}
